/* The copy the site ships with, in the same shape the CMS stores it.
 *
 * Every resolver call in the site passes one of these as its last
 * argument. Three things fall out of that:
 *
 *   1. The migration is phaseable. A page can be converted to read from
 *      Sanity before anyone has authored a single document — it renders
 *      byte-identically from here until content exists.
 *   2. An editor cannot break a page by blanking a field. The worst case
 *      is that it reverts to this text.
 *   3. The site still builds if Sanity is unreachable.
 *
 * Keep these strings byte-exact against what shipped: the curly
 * apostrophes, the em dashes, and the French narrow spaces are all
 * deliberate. When a field is authored in the CMS and verified, its entry
 * here becomes a safety net rather than the live value — leave it.
 */

// ─── Home ────────────────────────────────────────────────────────────

export const home = {
  heroLineOne: {
    english: 'Science Empowered',
    french: 'Science Propulsée'
  },
  heroLineTwo: {
    english: { before: 'Results', accent: 'Delivered', after: '' },
    french: { before: 'Résultats', accent: 'Livrés', after: '' }
  },
  heroSubcopy: {
    english:
      'Specialist in the sales and service of scientific equipment for applications in material and life sciences, including Electron Microscopy, 2D and 3D X-Ray Scanning, and Elemental Analysis.',
    french:
      'Spécialiste dans la vente et l’entretien d’équipements scientifiques pour les applications des matériaux et des sciences de la vie, y compris la microscopie électronique, le balayage aux rayons X 2D et 3D et l’analyse élémentaire.'
  },
  primaryCta: {
    label: { english: 'Learn More', french: 'En savoir plus' },
    route: '/about'
  },
  secondaryCta: {
    label: { english: 'Get in touch', french: 'Contactez-nous' },
    route: '/contact'
  },
  seo: {
    title: {
      english: 'Soquelec — Scientific Imaging & Analysis Systems',
      french: 'Soquelec — Microscopie Électronique et Analyse Élémentaire'
    },
    description: {
      english:
        'Scientific imaging and analysis systems distributed by Soquelec: electron microscopy, 2D/3D X-ray, elemental analysis, thin-film deposition. Sales, installation, and service across Canada.',
      french:
        'Systèmes d’imagerie et d’analyse scientifiques distribués par Soquelec : microscopie électronique, rayons X 2D/3D, analyse élémentaire, dépôt de couches minces. Vente, installation et service au Canada.'
    }
  }
};

// ─── About ───────────────────────────────────────────────────────────

export const about = {
  title: { english: 'About Soquelec', french: 'À Propos de Soquelec' },
  subtitle: {
    english: {
      before: 'Serving Canadian',
      accent: 'researchers',
      after: 'since 1974 with advanced scientific instrumentation and expert support.'
    },
    french: {
      before: 'Au service des',
      accent: 'chercheurs',
      after: 'canadiens depuis 1974 avec une instrumentation scientifique avancée et un soutien expert.'
    }
  },
  storyHeading: { english: 'Our Story', french: 'Notre Histoire' },
  storyBody: {
    english:
      'SOQUELEC was founded in 1974 by the late Jean-Pierre and Yette Slakmon. From its humble beginnings in the late seventies, SOQUELEC has grown to represent a wide variety of manufacturers and to provide its customers with a full range of quality products to meet their specific growing needs in an environment of rapidly evolving technology.',
    french:
      'SOQUELEC a été fondée en 1974 par le regretté Jean-Pierre et Yette Slakmon. Depuis ses modestes débuts à la fin des années soixante-dix, SOQUELEC s’est développée pour représenter un large éventail de manufacturiers et fournir à ses clients une gamme complète de produits de qualité répondant à leurs besoins spécifiques croissants dans un environnement évoluant rapidement.'
  },
  missionHeading: { english: 'Our Mission', french: 'Notre Mission' },
  teamHeading: { english: 'The Team', french: 'L’Équipe' },
  /* One ordered list — the About page's two columns are a render-time
     split, not two authored groups. Names are bilingual because
     post-nominals differ ("Eng." / "Ing."); where they don't, both
     fields carry the same value.
     NOTE: Stefano Rubino's French role does not translate his English
     one. Copied verbatim from what currently ships — worth correcting
     in the CMS, but not something to silently change here. */
  team: [
    { fullName: { english: 'Marc Slakmon', french: 'Marc Slakmon' },
      role: { english: 'President', french: 'Président' } },
    { fullName: { english: 'Philippe Semana, Eng.', french: 'Philippe Semana, Ing.' },
      role: { english: 'Vice President of Operations', french: 'Vice-Président des Opérations' } },
    { fullName: { english: 'Stefano Rubino, PhD.', french: 'Stefano Rubino, PhD.' },
      role: { english: 'Chief Technology Officer', french: 'Représentant des Ventes et du Service - Microscopie Électronique' } },
    { fullName: { english: 'Ariel Roytman, MSc.', french: 'Ariel Roytman, MSc.' },
      role: { english: 'Technical Sales Specialist – MicroCT', french: 'Spécialiste des Ventes Techniques - Microtomographe' } },
    { fullName: { english: 'Bushra Alam, PhD.', french: 'Bushra Alam, PhD.' },
      role: { english: 'Technical Sales Manager', french: 'Chargée des Ventes Techniques' } },
    { fullName: { english: 'Dakota Binkley, MASc.', french: 'Dakota Binkley, MASc.' },
      role: { english: 'Sales Representative', french: 'Représentante des Ventes' } },
    { fullName: { english: 'Mohamed Benamrou', french: 'Mohamed Benamrou' },
      role: { english: 'Controller', french: 'Contrôleur' } },
    { fullName: { english: 'Eman Donaire', french: 'Eman Donaire' },
      role: { english: 'Office Coordinator', french: 'Coordonnateur de Bureau' } }
  ],
  seo: {
    title: { english: 'About — Soquelec', french: 'À propos — Soquelec' },
    description: {
      english:
        'Soquelec, Montréal-based distributor since 1974 of scientific equipment for electron microscopy, X-ray imaging, elemental analysis, and thin-film deposition. Sales, installation, and service across Canada.',
      french:
        'Soquelec, distributeur montréalais depuis 1974 d’équipements scientifiques pour la microscopie électronique, l’imagerie par rayons X, l’analyse élémentaire et le dépôt de couches minces. Vente, installation et service partout au Canada.'
    }
  }
};

// ─── Equipment ───────────────────────────────────────────────────────

export const equipments = {
  title: { english: 'Equipment Offered', french: 'Instrumentation Offerte' },
  subtitle: {
    english: {
      before: 'Explore our',
      accent: 'portfolio',
      after: 'of scientific instruments and accessories from leading manufacturers.'
    },
    french: {
      before: 'Explorez notre',
      accent: 'gamme',
      after: 'd’instruments scientifiques et accessoires de fabricants de premier plan.'
    }
  },
  seo: {
    title: { english: 'Soquelec — Equipment', french: 'Soquelec — Instrumentation' },
    description: {
      english:
        'Scientific instruments and accessories distributed by Soquelec across Canada: electron microscopy, X-ray imaging and irradiation, optical imaging, elemental analysis, thin-film deposition, and specialty lasers from JEOL, Gatan, Quorum, RMC Boeckeler, KUBTEC, Spectral, Annealsys, and others.',
      french:
        'Instruments scientifiques et accessoires distribués par Soquelec à travers le Canada : microscopie électronique, imagerie et irradiation aux rayons X, imagerie optique, analyse élémentaire, dépôt de couches minces et lasers spécialisés de JEOL, Gatan, Quorum, RMC Boeckeler, KUBTEC, Spectral, Annealsys et autres.'
    }
  }
};

// ─── Consumables ─────────────────────────────────────────────────────

export const consumables = {
  title: { english: 'Consumable Categories', french: 'Types de Fourniture' },
  subtitle: {
    english: {
      before: 'Browse our',
      accent: 'catalog',
      after: 'of consumables and supplies for electron microscopy and laboratory use.'
    },
    french: {
      before: 'Parcourez notre',
      accent: 'catalogue',
      after: 'de fournitures et consommables pour la microscopie électronique et le laboratoire.'
    }
  },
  seo: {
    title: { english: 'Consumables — Soquelec', french: 'Fournitures — Soquelec' },
    description: {
      english:
        'Scientific consumables distributed by Soquelec across Canada: TEM/SEM grids, AFM probes, cantilevers, sample-preparation tools, cryo-handling, and more for materials and life-science laboratories.',
      french:
        'Consommables scientifiques distribués par Soquelec au Canada : grilles TEM/SEM, sondes AFM, cantilevers, outils de préparation d’échantillons, manipulation cryogénique et plus pour laboratoires des matériaux et sciences de la vie.'
    }
  }
};

// ─── Manufacturers ───────────────────────────────────────────────────

export const manufacturers = {
  title: { english: 'Our Manufacturers', french: 'Nos Manufacturiers' },
  subtitle: {
    english: {
      before: 'We',
      accent: 'partner',
      after: 'with industry-leading manufacturers to bring you the best in scientific instrumentation.'
    },
    french: {
      before: 'Nous',
      accent: 'collaborons',
      after: 'avec des fabricants de premier plan pour vous offrir le meilleur en instrumentation scientifique.'
    }
  },
  cardCtaLabel: { english: 'Official Site', french: 'Site Officiel' },
  seo: {
    title: { english: 'Manufacturers — Soquelec', french: 'Manufacturiers — Soquelec' },
    description: {
      english:
        'Scientific equipment manufacturers Soquelec distributes across Canada: JEOL, Gatan, Quorum, KUBTEC, NanoWorld, NANOSENSORS, Protochips, RMC Boeckeler, Annealsys, Haskris, Spectral, IDES, and more.',
      french:
        'Fabricants d’équipements scientifiques distribués par Soquelec au Canada : JEOL, Gatan, Quorum, KUBTEC, NanoWorld, NANOSENSORS, Protochips, RMC Boeckeler, Annealsys, Haskris, Spectral, IDES et autres.'
    }
  }
};

// ─── Contact ─────────────────────────────────────────────────────────

export const contact = {
  title: { english: 'Get in Touch', french: 'Nous Joindre' },
  subtitle: {
    english: {
      before: 'Questions or quotes? We respond within',
      accent: 'a business day',
      after: '.'
    },
    french: {
      before: 'Questions ou devis ? Nous répondons dans',
      accent: 'un jour ouvrable',
      after: '.'
    }
  },
  /* Wording only — the phone number, email, and address themselves live
     in `site` below, so a change of number is one edit. `kind` is how a
     row finds its value. */
  methods: [
    { kind: 'call',
      label: { english: 'Call', french: 'Téléphone' },
      meta: { english: 'Mon–Fri, 9:00 AM – 5:00 PM ET', french: 'Lun–Ven, 9 h – 17 h HE' } },
    { kind: 'email',
      label: { english: 'Email', french: 'Courriel' },
      meta: { english: 'Sales, service, and support', french: 'Ventes, service et soutien' } },
    { kind: 'visit',
      label: { english: 'Visit', french: 'Bureaux' },
      meta: { english: 'Complex Dompark — 4th floor', french: 'Complexe Dompark — 4ᵉ étage' } }
  ],
  mapLinkLabel: { english: 'Open in Google Maps', french: 'Ouvrir dans Google Maps' },
  mapTitle: {
    english: 'Soquelec office location',
    french: 'Emplacement des bureaux Soquelec'
  },
  seo: {
    title: { english: 'Soquelec — Contact', french: 'Soquelec — Contactez-nous' },
    description: {
      english:
        'Contact Soquelec — phone, email, and our Montréal office on rue Saint-Patrick. Questions and quote requests are answered within one business day.',
      french:
        'Contactez Soquelec — téléphone, courriel et nos bureaux montréalais sur la rue Saint-Patrick. Les questions et demandes de devis reçoivent une réponse dans un jour ouvrable.'
    }
  }
};

// ─── My Inquiry ──────────────────────────────────────────────────────

export const inquiry = {
  title: { english: 'My Inquiry', french: 'Ma Demande' },
  subtitle: {
    english: { before: 'Review your selections and request a', accent: 'quote', after: '.' },
    french: { before: 'Révisez vos sélections et demandez un', accent: 'devis', after: '.' }
  },
  emptyHeading: {
    english: { before: 'Build your', accent: 'inquiry', after: '' },
    french: { before: 'Composez votre', accent: 'demande', after: '' }
  },
  emptyBody: {
    english:
      'Browse our consumables catalog to add items. Our team will follow up with pricing and availability.',
    french:
      'Parcourez notre catalogue de consommables pour ajouter des articles. Notre équipe vous répondra avec les prix et la disponibilité.'
  },
  successHeading: {
    english: { before: 'Request', accent: 'received', after: '.' },
    french: { before: 'Demande', accent: 'reçue', after: '.' }
  },
  /* Four complete sentences — the French agrees at two points from one
     count ("article{s} sélectionné{s}"), which a suffix rule cannot do. */
  successBody: {
    englishOne: 'Our team will be in touch shortly about your {count} selected item.',
    englishOther: 'Our team will be in touch shortly about your {count} selected items.',
    frenchOne: 'Notre équipe vous contactera sous peu au sujet de votre {count} article sélectionné.',
    frenchOther: 'Notre équipe vous contactera sous peu au sujet de vos {count} articles sélectionnés.'
  },
  successBodyNoItems: {
    english: 'Our team will be in touch shortly about your inquiry.',
    french: 'Notre équipe vous contactera sous peu au sujet de votre demande.'
  },
  seo: {
    title: { english: 'Soquelec — My Inquiry', french: 'Soquelec — Ma Demande' },
    description: {
      english: 'Review your selected consumables and request a quote from Soquelec’s team.',
      french: 'Révisez vos consommables sélectionnés et demandez un devis à l’équipe Soquelec.'
    }
  }
};

// ─── 404 ─────────────────────────────────────────────────────────────

export const notFound = {
  heading: {
    english: { before: 'Off the', accent: 'map', after: '' },
    french: { before: 'Hors des', accent: 'sentiers', after: '' }
  },
  body: {
    english: 'You’ve landed on a page that doesn’t exist — or has moved on.',
    french: 'Cette page n’existe plus — ou a été déplacée.'
  },
  ctaLabel: { english: 'Back home', french: 'Retour à l’accueil' },
  fieldCodeLabel: { english: 'not found', french: 'introuvable' },
  seo: {
    title: { english: 'Soquelec — Page not found', french: 'Soquelec — Page introuvable' },
    description: {
      english:
        'The page you’re looking for doesn’t exist. Head back to explore Soquelec’s scientific instrumentation.',
      french:
        'Cette page n’existe pas. Retournez explorer l’instrumentation scientifique de Soquelec.'
    }
  }
};

// ─── Site settings ───────────────────────────────────────────────────

export const site = {
  legalName: { english: 'LTD.', french: 'LTÉE.' },
  rightsReserved: { english: 'All rights reserved.', french: 'Tous droits réservés.' },
  /* The canonical mission statement, as rendered on the About page.
     Note this is NOT the same string as home.heroSubcopy — the hero
     opens "Specialist in…" as a fragment, this opens "SOQUELEC
     specializes in…" as a full sentence. They say the same thing in
     different registers, so each surface keeps its own wording rather
     than being forced to share one. */
  missionStatement: {
    english:
      'SOQUELEC specializes in the sales and service of scientific equipment for applications in material and life sciences, including Electron Microscopy, 2D and 3D X-Ray Scanning, and Elemental Analysis.',
    french:
      'SOQUELEC est spécialisé dans la vente et l’entretien d’équipements scientifiques pour les applications des matériaux et des sciences de la vie, y compris la microscopie électronique, le balayage aux rayons X 2D et 3D et l’analyse élémentaire.'
  },
  phone: '+1 (514) 482-6427',
  email: 'sales@soquelec.com',
  address: {
    lines: ['5524 rue Saint-Patrick, Suite 405', 'Montréal, QC H4E 1A8'],
    /* Pinned to the exact query string the site has always sent to
       Google Maps. Deriving it from the lines above produces a URL that
       differs in encoding (commas escaped, "Montréal" accented rather
       than "Montreal") — Maps would very likely still resolve it, but
       "very likely" is not a good enough reason to change a URL that
       currently drops the pin in the right place. Clear this field to
       fall back to derivation. */
    mapQuery: '5524+rue+Saint-Patrick+Suite+405+Montreal+QC+H4E+1A8'
  },
  socialLinks: [
    { platform: 'facebook', url: 'https://www.facebook.com/soquelecltd' },
    { platform: 'x', url: 'https://x.com/soquelec' },
    { platform: 'linkedin', url: 'https://www.linkedin.com/company/soquelec-ltd' }
  ],
  navItems: [
    { label: { english: 'Equipment', french: 'Instrumentation' }, route: '/equipments', placement: 'both' },
    { label: { english: 'Consumables', french: 'Fournitures' }, route: '/consumables', placement: 'both' },
    { label: { english: 'Manufacturers', french: 'Manufacturiers' }, route: '/manufacturers', placement: 'both' },
    { label: { english: 'About', french: 'À Propos' }, route: '/about', placement: 'both' },
    { label: { english: 'Contact', french: 'Contactez-nous' }, route: '/contact', placement: 'both' },
    { label: { english: 'My Inquiry', french: 'Ma Demande' }, route: '/inquiry', placement: 'cta' }
  ]
};

// ─── Shared interface strings ────────────────────────────────────────

export const ui = {
  // Catalog & search
  searchPlaceholder: { english: 'Search', french: 'Rechercher' },
  resultCount: {
    englishOne: '{count} result',
    englishOther: '{count} results',
    frenchOne: '{count} article',
    frenchOther: '{count} articles'
  },
  sortByLabel: { english: 'Sort by', french: 'Trier par' },
  sortCodeAsc: { english: 'Code Ascending', french: 'Code en ordre Croissant' },
  sortCodeDesc: { english: 'Code Descending', french: 'Code en ordre Décroissant' },
  sortNameAsc: { english: 'Name Ascending', french: 'Nom en ordre Croissant' },
  sortNameDesc: { english: 'Name Descending', french: 'Nom en ordre Décroissant' },
  allConsumablesTitle: { english: 'All Consumables', french: 'Toutes Fournitures' },
  searchResultsTitle: {
    english: 'Result(s) for: ‘{query}’',
    french: 'Résultat(s) pour : ‘{query}’'
  },
  browseFullCatalog: {
    english: 'Browse the Full Catalog',
    french: 'Parcourir le Catalogue Complet'
  },

  // Forms
  fieldFullName: { english: 'Full Name', french: 'Nom Complet' },
  fieldCompany: { english: 'Company Name', french: 'Entreprise' },
  fieldEmail: { english: 'Email', french: 'Courriel' },
  fieldPhone: { english: 'Phone Number', french: 'Numéro de Téléphone' },
  fieldInquiryDetails: { english: 'Inquiry Details', french: 'Détails de la Demande' },
  fieldQuestion: { english: 'Your Question', french: 'Votre Question' },
  errNameRequired: { english: 'Full name is required', french: 'Le nom complet est requis' },
  errNameTooShort: {
    english: 'Name must be at least 2 characters',
    french: 'Le nom doit contenir au moins 2 caractères'
  },
  errEmailRequired: { english: 'Email is required', french: 'Le courriel est requis' },
  errEmailInvalid: { english: 'Enter a valid email', french: 'Entrez un courriel valide' },
  errPhoneRequired: {
    english: 'Phone number is required',
    french: 'Le numéro de téléphone est requis'
  },
  errPhoneInvalid: { english: 'Enter a valid phone number', french: 'Entrez un numéro valide' },

  // Messages
  alertEmptyInquiry: {
    english: 'Your inquiry is empty. Add consumables before requesting a quote.',
    french: 'Votre demande est vide. Ajoutez des consommables avant de soumettre.'
  },
  alertSendFailed: {
    english: 'Request didn’t go through. Try again, or reach us directly if it persists.',
    french:
      'La demande n’a pas été envoyée. Réessayez, ou contactez-nous directement si le problème persiste.'
  },
  alertNetworkError: {
    english: 'Something went wrong. Try again.',
    french: 'Une erreur est survenue. Réessayez.'
  },
  alertRequestReceived: {
    english: 'Request received. Our team will be in touch shortly.',
    french: 'Demande reçue. Notre équipe vous contactera sous peu.'
  },
  alertQuestionReceived: {
    english: 'Question received. Our team will be in touch shortly.',
    french: 'Question reçue. Notre équipe vous répondra sous peu.'
  },

  // Product pages
  productCodeLabel: { english: 'Product Code', french: 'Numéro de Produit' },
  contentsSingle: { english: 'Contents: 1 unit', french: 'Contenu : 1 unité' },
  contentsPerPack: {
    english: 'Contents: {amount} per pack',
    french: 'Contenu : {amount} par paquet'
  },
  addToInquiry: { english: 'Add to My Inquiry', french: 'Ajouter à Ma Demande' },
  askQuestionHeading: {
    english: 'Have a question about this item?',
    french: 'Une question sur ce produit ?'
  },
  askQuestionSubmit: { english: 'Ask a Question', french: 'Poser une Question' },
  itemNotFound: { english: 'Item not found', french: 'Produit introuvable' },
  goBackLabel: { english: 'Go back', french: 'Retour' },
  questionSuccessHeading: {
    english: { before: 'Question', accent: 'received', after: '.' },
    french: { before: 'Question', accent: 'reçue', after: '.' }
  },
  questionSuccessBody: {
    english: 'Our team will be in touch shortly with an answer.',
    french: 'Notre équipe vous répondra sous peu.'
  },
  productSeoTitle: {
    english: '{name} ({code}) — Soquelec',
    french: '{name} ({code}) — Soquelec'
  },
  productSeoDescription: {
    english:
      '{name}, product code {code}. Scientific consumable distributed by Soquelec for electron microscopy, AFM, and materials-analysis laboratories across Canada. Request pricing or ask a question directly.',
    french:
      '{name}, code {code}. Consommable scientifique distribué par Soquelec pour la microscopie électronique, l’AFM et les laboratoires d’analyse des matériaux au Canada. Demandez un prix ou posez une question.'
  },

  // Empty states & shared buttons
  browseConsumables: { english: 'Browse Consumables', french: 'Voir les Consommables' },
  reachYouHeading: { english: 'How can we reach you?', french: 'Comment vous joindre ?' },
  requestQuoteSubmit: { english: 'Request a Quote', french: 'Demander un Devis' }
};

/* Single namespace so call sites read `FB.home.heroLineOne` rather than
   importing eight things. */
export const FB = {
  home,
  about,
  equipments,
  consumables,
  manufacturers,
  contact,
  inquiry,
  notFound,
  site,
  ui
};

export default FB;
